import P, { P2 } from "../../components/Paragraph";
import Button from "../../components/Button";
import Input from "./Input";
import TextArea from "./TextArea";
import ChipSelector from "./ChipSelector";
import DropdownSelector from "./DropdownSelector";
import ListSelector from "./ListSelector";
import Spectrum from "../spectrum/Spectrum";
import Loader from "../../components/Loader";
import classNames from "classnames";
import useLang from "../../hooks/useLang";
import useFormInputText from "./hooks/useFormInputText";
import useFormSelector from "./hooks/useFormSelector";
import useFormMultiselector from "./hooks/useFormMultiselector";
import useFormListSelector from "./hooks/useFormListSelector";
import useFormMatrixSelector from "./hooks/useFormMatrixSelector";
import useFormSpectrumEditor from "./hooks/useFormSpectrumEditor";
import styles from "./input.module.css";
/*
Current input types:
button, checkbox, color, date, datetime-local, email, file, hidden, image, month, number, password, radio,range, reset, search, submit, tel, text,time, url, week
Default input type: text

Other form elements:
<textarea/>
<select><option/></select>
*/

const FormConstantText = ({ value, inputWidth, noLabel, invalid, ...rest }) => {
  return (
    <div
      className={classNames(styles.constantText, styles[inputWidth])}
      {...rest}
    >
      <P {...rest}>{value}</P>
    </div>
  );
};

const FormInputText = ({ id, value: initialValue, onChange, ...rest }) => {
  const inputAttributes = useFormInputText({ id, initialValue, onChange });
  return <Input {...inputAttributes} {...rest} />;
};

const FormTextArea = ({
  id,
  value: initialValue,
  onChange,
  invalid,
  ...rest
}) => {
  const inputAttributes = useFormInputText({ id, initialValue, onChange });
  return <TextArea {...inputAttributes} {...rest} />;
};

const FormChipRadio = ({
  id,
  value: initialValue,
  options,
  onChange,
  idAsValue,
  invalid,
  ...rest
}) => {
  const selectorAttributes = useFormSelector({
    id,
    initialValue,
    options,
    onChange,
    idAsValue,
  });
  return <ChipSelector {...selectorAttributes} {...rest} />;
};

const FormChipMultiselector = ({
  id,
  value: initialValue,
  options,
  onChange,
  invalid,
  ...rest
}) => {
  const selectorAttributes = useFormMultiselector({
    id,
    initialValue,
    options,
    onChange,
  });
  return <ChipSelector {...selectorAttributes} {...rest} />;
};

const FormListSelector = ({
  id,
  idAsValue,
  value: initialValue,
  options,
  onChange,
  link,
  invalid,
  label,
  loading,
  noOptionsLabel,
  ...rest
}) => {
  const selectorAttributes = useFormListSelector({
    id,
    idAsValue,
    initialValue,
    options,
    onChange,
    label,
  });
  if (loading) {
    return <Loader small />;
  }
  if (Array.isArray(options) && options.length === 0) {
    return <P2 style={{ fontStyle: "italic" }}>{noOptionsLabel}</P2>;
  }
  return <ListSelector {...selectorAttributes} {...rest} />;
};

const FormMatrixSelector = ({
  id,
  valueData,
  value: initialValue,
  options,
  onChange,
  link,
  inputs,
  invalid,
  addButtonCaption,
  pageOnly,
  ...rest
}) => {
  const { handleAdd, handleRemove, inputGroups } = useFormMatrixSelector({
    id,
    valueData,
    value: initialValue,
    options,
    onChange,
    link,
    inputs,
    ...rest,
  });
  const lang = useLang();
  return (
    <>
      {inputGroups?.map((dataItem, dataItemIdx) => (
        <div
          key={`${dataItemIdx}_${JSON.stringify(dataItem)}`}
          style={{ display: "flex", gap: "var(--gap-base)" }}
        >
          {inputs.map((inputItem) => {
            return (
              <FormInput
                key={inputItem.id || JSON.stringify(inputItem)}
                value={dataItem[inputItem.id]}
                {...inputItem}
                id={`${id}___${dataItemIdx}___${inputItem.id}`}
                onChange={inputItem.id != null ? onChange : () => {}}
                noLabel
              />
            );
          })}
          <div style={{ display: "flex", flexGrow: "1" }} />
          <Button
            icon="trashIcon"
            inline
            onClick={(event) => {
              event.preventDefault();
              handleRemove({ id, idx: dataItemIdx });
            }}
          />
        </div>
      ))}
      <Button
        onClick={(event) => {
          event.preventDefault();
          handleAdd();
        }}
      >
        {addButtonCaption ? addButtonCaption : lang("common.add")}
      </Button>
    </>
  );
};

const FormDropdownSelector = ({
  id,
  idAsValue,
  value: initialValue,
  options,
  onChange,
  invalid,
  ...rest
}) => {
  const selectorAttributes = useFormSelector({
    id,
    idAsValue,
    initialValue,
    options,
    onChange,
  });
  return <DropdownSelector {...selectorAttributes} {...rest} />;
};

const FormSpectrumEditor = ({
  id,
  value,
  options,
  onChange,
  idAsValue,
  invalid,
  data,
  ...rest
}) => {
  const spectrumAttributes = useFormSpectrumEditor({
    initialValue: data,
    onChange,
  });
  return <Spectrum {...spectrumAttributes} {...rest} />;
};

const FormInput = ({
  type,
  formOnly,
  formData,
  displayValue,
  formItem,
  link,
  isValid,
  ...rest
}) => {
  const invalid =
    formData != null && typeof isValid === "function"
      ? !isValid(formData[rest?.id])
      : false;
  if (type === "label") return <FormConstantText {...{ ...rest, invalid }} />;
  if (type === "textarea") return <FormTextArea {...{ ...rest, invalid }} />;
  if (type === "chip-radio") return <FormChipRadio {...{ ...rest, invalid }} />;
  if (type === "chip-multiselector")
    return <FormChipMultiselector {...{ ...rest, invalid }} />;
  if (type === "dropdown-selector")
    return <FormDropdownSelector {...{ ...rest, invalid }} />;
  if (type === "list" || type === "multiselector")
    return <FormListSelector {...{ ...rest, invalid }} />;
  if (type === "matrix")
    return <FormMatrixSelector formData={formData} {...{ ...rest, invalid }} />;
  if (type === "spectrum")
    return <FormSpectrumEditor formData={formData} {...{ ...rest, invalid }} />;
  if (type === "spectrumPatchable")
    return (
      <FormSpectrumEditor
        formData={formData}
        patchOnly={["selectivity", "laterality"]}
        {...{ ...rest, invalid }}
      />
    );
  if (type === "spectraArray")
    return <FormSpectrumEditor formData={formData} {...{ ...rest, invalid }} />;
  return <FormInputText type={type} {...{ ...rest, invalid }} />;
};

export default FormInput;
